import {
  SimilarSearchJobSummary,
  buildCanonicalJobListingPath,
  buildCanonicalJobListingUrl,
} from '@/utils/jobListingHelper';
import cx from 'classnames';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { pushGtmEvent } from 'utils/gtm';

import styles from './ExploreSimilarSearches.module.scss';

const MAX_CARDS_PER_TAB = 8;

type SimilarSearchTab = 'location' | 'type';

interface SimilarSearchCard {
  id: string;
  label: string;
  count: number;
  href: string;
  path: string;
}

export interface ExploreSimilarSearchesProps {
  jobs: SimilarSearchJobSummary[];
  origin: string;
  grade?: string;
  speciality?: string;
  state?: string;
  workType?: string;
}

interface CanonicalListingFilters {
  grade?: string;
  speciality?: string;
  location?: string;
  workType?: string;
}

/**
 * Groups jobs by the value returned by `getGroupValue`, counts them, drops the current
 * filter value (so we only surface *other* searches) and returns the top cards by count.
 */
const buildCards = (
  jobs: SimilarSearchJobSummary[],
  getGroupValue: (job: SimilarSearchJobSummary) => string | undefined,
  currentValue: string | undefined,
  buildLabel: (value: string) => string,
  buildFilters: (value: string) => CanonicalListingFilters,
  origin: string,
): SimilarSearchCard[] => {
  const counts = new Map<string, number>();

  jobs.forEach(job => {
    const value = getGroupValue(job);
    if (!value || value === currentValue) {
      return;
    }
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, MAX_CARDS_PER_TAB)
    .map(([value, count]) => {
      const filters = buildFilters(value);
      return {
        id: value,
        label: buildLabel(value),
        count,
        href: buildCanonicalJobListingUrl(filters, origin),
        path: buildCanonicalJobListingPath(filters),
      };
    });
};

export const ExploreSimilarSearches = ({
  jobs,
  origin,
  grade,
  speciality,
  state,
  workType,
}: ExploreSimilarSearchesProps) => {
  const [activeTab, setActiveTab] = useState<SimilarSearchTab>('location');

  const locationCards = useMemo(
    () =>
      buildCards(
        jobs,
        job => job.locationDisplay || job.location,
        state,
        value => (grade ? `${grade} jobs in ${value}` : `Doctor jobs in ${value}`),
        value => ({ grade, location: value, workType }),
        origin,
      ),
    [jobs, state, grade, workType, origin],
  );

  const typeCards = useMemo(
    () =>
      buildCards(
        jobs,
        job => job.speciality,
        speciality,
        value => (grade ? `${grade} ${value} jobs` : `${value} jobs`),
        value => ({ grade, speciality: value, workType }),
        origin,
      ),
    [jobs, speciality, grade, workType, origin],
  );

  if (!locationCards.length && !typeCards.length) {
    return null;
  }

  const handleTabClick = (tab: SimilarSearchTab) => {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    pushGtmEvent({
      event: 'similar_search_tab',
      element: 'Explore Similar Searches Tab',
      event_label: tab === 'location' ? 'by_location' : 'by_type',
    });
  };

  const handleCardClick = (card: SimilarSearchCard) => {
    pushGtmEvent({
      event: 'similar_search_click',
      element: 'Explore Similar Searches Card',
      link_text: card.label,
      link_url: card.href,
    });
  };

  const renderPanel = (tab: SimilarSearchTab, cards: SimilarSearchCard[]) => (
    <div
      role="tabpanel"
      id={`explore-similar-searches-panel-${tab}`}
      aria-labelledby={`explore-similar-searches-tab-${tab}`}
      aria-hidden={activeTab !== tab}
      className={cx(styles.panel, { [styles.panelHidden]: activeTab !== tab })}
      data-testid={`explore-similar-searches-panel-${tab}`}
    >
      {cards.map(card => (
        // href is the relative canonical path so next/link's own client-side routing
        // (which normalizes/matches same-origin hrefs against a dummy base URL - see
        // resolveHref in next/dist/client/resolve-href.js) works correctly; `as` renders
        // the absolute canonical URL in the DOM and browser address bar for SEO/crawlability
        // without going through that same-origin normalization.
        <Link
          key={card.id}
          href={card.path}
          as={card.href}
          className={styles.card}
          onClick={() => handleCardClick(card)}
        >
          <span className={styles.cardLabel}>{card.label}</span>
          <span className={styles.cardCount}>{card.count}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <div className={styles.exploreSimilarSearches} data-testid="explore-similar-searches">
      <div className={styles.header}>
        <h2 className={styles.heading}>
          <span className={styles.accent}>Explore</span> similar searches
        </h2>
        <p className={styles.description}>
          Browse related specialties, locations and job types to find more opportunities that match
          what you're looking for.
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.tabBar} role="tablist" aria-label="Explore similar searches">
          <button
            type="button"
            role="tab"
            id="explore-similar-searches-tab-location"
            aria-selected={activeTab === 'location'}
            aria-controls="explore-similar-searches-panel-location"
            className={cx(styles.tabButton, {
              [styles.tabButtonActive]: activeTab === 'location',
            })}
            onClick={() => handleTabClick('location')}
          >
            By location
          </button>
          <button
            type="button"
            role="tab"
            id="explore-similar-searches-tab-type"
            aria-selected={activeTab === 'type'}
            aria-controls="explore-similar-searches-panel-type"
            className={cx(styles.tabButton, { [styles.tabButtonActive]: activeTab === 'type' })}
            onClick={() => handleTabClick('type')}
          >
            By type
          </button>
        </div>

        <div className={styles.cardGrid}>
          {renderPanel('location', locationCards)}
          {renderPanel('type', typeCards)}
        </div>
      </div>
    </div>
  );
};

export default ExploreSimilarSearches;
